"use client"

/**
 * Live seeding calculator form. Mirrors the aeration-form pattern — no
 * submit button, useMemo-driven calculation, two-column desktop layout
 * with a sticky preview rail on the right and a compact preview + bottom
 * dock on mobile.
 *
 * Full-engine `calculateSeedingWindows` runs on every input change; the
 * verdict layer (`buildSeedingViewPlan`) maps that onto the 5-state
 * preview copy.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import {
  SEEDING_GRASS_CARDS,
  SEEDING_GRASS_DEFAULT_COUNT,
  SEEDING_REGION_DEFAULTS,
  buildSeedingViewPlan,
  findSeedingCard,
  fmtMonthDay,
  type SeedingGrassCard,
} from "@/lib/seeding-plan"
import {
  calculateSeedingWindows,
  type SeedingResult,
  type SeedingWindow,
} from "@/lib/seeding-calculator"
import { IDENTIFY_CTA, identifyCtaProps } from "@/lib/identify-cta"
import { analytics, trackEvent } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { ZipCodeInput } from "@/components/ui/zip-code-input"
import { SeedingPreview } from "@/components/tools/seeding-preview"
import { setBottomDockHeight, useAdDockInset } from "@/hooks/use-bottom-dock-inset"
import { useCalculatorAnalytics } from "@/hooks/useCalculatorAnalytics"
import { useSavedInputs } from "@/hooks/use-saved-inputs"
import { getRegionFromZipCode, toLegacyRegion } from "@/data/regional-climate"
import {
  generateGoogleCalendarUrl,
  downloadICSFile,
  type CalendarEvent,
} from "@/lib/calendar-utils"

const S_BG = "#ffffff"
const S_INK = "#111827"
const S_INK2 = "#374151"
const S_RULE = "#e5e7eb"
const S_BRAND = "#16a34a"
const S_BRAND_700 = "#15803d"
const S_BRAND_50 = "#f0fdf4"
const S_BRAND_100 = "#dcfce7"
const S_MUTED = "#6b7280"
const S_PAPER_DEEP = "#f3f4f6" // gray-100 — faint backdrop behind the carded sections
const S_FONT = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`

// ── Inline icons ───────────────────────────────────────────────────
function Ico({ size = 14, children }: { size?: number; children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}
const IcoLeaf = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M5 19c8 0 14-6 14-14C9 5 5 11 5 19z" />
    <path d="M5 19l9-9" />
  </Ico>
)
const IcoPin = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M12 21s-7-7.5-7-12a7 7 0 0114 0c0 4.5-7 12-7 12z" />
    <circle cx="12" cy="9" r="2.5" />
  </Ico>
)
const IcoCal = (p: { size?: number }) => (
  <Ico {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 9h18M8 3v4M16 3v4" />
  </Ico>
)
const IcoChev = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M6 9l6 6 6-6" />
  </Ico>
)
const IcoArrow = (p: { size?: number }) => (
  <Ico {...p}>
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </Ico>
)
const IcoLock = (p: { size?: number }) => (
  <Ico {...p}>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 018 0v4" />
  </Ico>
)
const IcoRuler = (p: { size?: number }) => (
  <Ico {...p}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 8h3M3 12h5M3 16h3M8 16v5M12 19v2M16 16v5" />
  </Ico>
)
const IcoCalDot = (p: { size?: number }) => (
  <Ico {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 9h18M8 3v4M16 3v4" />
    <circle cx="12" cy="14" r="1.5" fill="currentColor" />
  </Ico>
)

function GrassGlyph({
  active,
  season,
}: {
  active: boolean
  season: SeedingGrassCard["season"]
}) {
  const ink = active ? S_BRAND : S_INK2
  return (
    <span
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        background: active ? S_BRAND_100 : S_BRAND_50,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
      aria-hidden="true"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke={ink}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 21h16" />
        {season === "cool" ? (
          <>
            <path d="M7 21c0-5 1-8 3-10" />
            <path d="M12 21c0-6 1-9 3-12" />
            <path d="M17 21c0-4 0-7 2-9" />
            <circle cx="9" cy="9" r="1.2" fill={ink} stroke="none" />
          </>
        ) : (
          <>
            <path d="M6 21c0-3 1-5 3-6" />
            <path d="M10 21c0-4 1-6 3-8" />
            <path d="M14 21c0-5 2-7 4-8" />
            <path d="M19 21c0-2 0-4 1-5" />
          </>
        )}
      </svg>
    </span>
  )
}

function StepCircle({ step, accent }: { step: number; accent?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 27,
        height: 27,
        flexShrink: 0,
        borderRadius: "50%",
        background: accent ? S_BRAND_50 : S_PAPER_DEEP,
        border: accent ? `1.5px solid ${S_BRAND}` : `1px solid ${S_RULE}`,
        boxShadow: accent ? `0 0 0 3px ${S_BRAND}22` : "none",
        color: accent ? S_BRAND_700 : S_INK,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {step}
    </span>
  )
}

function Label({
  icon,
  children,
  hint,
  step,
  accent,
}: {
  icon?: React.ReactNode
  children: React.ReactNode
  hint?: React.ReactNode
  step?: number
  accent?: boolean
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        marginBottom: 10,
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {step != null && <StepCircle step={step} accent={accent} />}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: S_BRAND_700,
          }}
        >
          {icon}
          {children}
        </div>
      </div>
      {hint && (
        <div style={{ fontSize: 13, color: S_MUTED, textAlign: "right" }}>{hint}</div>
      )}
    </div>
  )
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${S_RULE}`,
        borderRadius: 12,
        padding: 18,
      }}
    >
      {children}
    </div>
  )
}

function GrassField({
  value,
  onChange,
}: {
  value: string
  onChange: (engineKey: string) => void
}) {
  const [showAll, setShowAll] = useState(false)
  const cards = showAll
    ? SEEDING_GRASS_CARDS
    : SEEDING_GRASS_CARDS.slice(0, SEEDING_GRASS_DEFAULT_COUNT)
  const current = findSeedingCard(value) ?? SEEDING_GRASS_CARDS[0]
  // Denominator for the identify escape hatch below. Without an impression the
  // link's clicks have no rate to compare against the other identify surfaces.
  const identifyImpressionRef = useImpressionEvent<HTMLAnchorElement>(
    IDENTIFY_CTA.location.toolSeedingFormIdentify,
    { surface: 'tool_seeding_form' },
    { emit: analytics.ctaSurfaceShown },
  )
  return (
    <div>
      <Label step={2} icon={<IcoLeaf size={13} />} hint={current.tag}>
        Grass type
      </Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
        {cards.map((c) => {
          const active = c.engineKey === value
          return (
            <button
              type="button"
              key={c.engineKey}
              onClick={() => onChange(c.engineKey)}
              aria-pressed={active}
              style={{
                padding: "10px 12px",
                background: active ? S_BRAND_50 : "#fff",
                color: S_INK,
                border: `1px solid ${active ? S_BRAND : S_RULE}`,
                borderRadius: 10,
                cursor: "pointer",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 10,
                transition: "all 150ms var(--ease-standard)",
              }}
            >
              <span className="hidden xs:inline-flex">
                <GrassGlyph active={active} season={c.season} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, overflowWrap: "break-word" }}>
                  {c.name}
                </div>
                <div style={{ fontSize: 12, color: S_MUTED, marginTop: 1 }}>
                  {c.tag} · {c.soilTemp}
                </div>
              </div>
            </button>
          )
        })}
      </div>
      {!showAll && SEEDING_GRASS_CARDS.length > SEEDING_GRASS_DEFAULT_COUNT && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          style={{
            marginTop: 10,
            background: "transparent",
            border: "none",
            padding: "4px 0",
            fontSize: 13,
            color: S_BRAND_700,
            fontWeight: 500,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          +{SEEDING_GRASS_CARDS.length - SEEDING_GRASS_DEFAULT_COUNT} more grass types{" "}
          <IcoChev size={12} />
        </button>
      )}
      <div style={{ marginTop: 8 }}>
        <a
          ref={identifyImpressionRef}
          href="/what-kind-of-grass-do-i-have"
          onClick={() => {
            trackEvent('cta_clicked', {
              ...identifyCtaProps(IDENTIFY_CTA.location.toolSeedingFormIdentify, {
                text: 'Not sure what you have? Identify it',
              }),
              surface: 'tool_seeding_form',
            })
          }}
          style={{
            fontSize: 13,
            color: S_MUTED,
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          Not sure what you have?
          <span style={{ color: S_BRAND_700, marginLeft: 4 }}>Identify it →</span>
        </a>
      </div>
    </div>
  )
}

function OptionalFields({
  planningDate,
  onPlanningDateChange,
  lawnSize,
  onLawnSizeChange,
}: {
  planningDate: string
  onPlanningDateChange: (v: string) => void
  lawnSize: string
  onLawnSizeChange: (v: string) => void
}) {
  return (
    <div>
      <Label step={3} icon={<IcoCal size={13} />} hint="optional, sharpens timing">
        Timing &amp; size
      </Label>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
        }}
      >
      <div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: S_MUTED,
            marginBottom: 6,
          }}
        >
          <IcoCalDot size={12} /> Planning date
        </div>
        <input
          type="date"
          value={planningDate}
          onChange={(e) => onPlanningDateChange(e.target.value)}
          aria-label="Planning date"
          style={{
            width: "100%",
            padding: "10px 12px",
            border: `1px solid ${S_RULE}`,
            borderRadius: 8,
            background: "#fff",
            fontSize: 16,
            fontFamily: S_FONT,
            color: S_INK,
            minHeight: 44,
          }}
        />
        <div style={{ fontSize: 11.5, color: S_MUTED, marginTop: 4 }}>
          Defaults to today
        </div>
      </div>
      <div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: S_MUTED,
            marginBottom: 6,
          }}
        >
          <IcoRuler size={12} /> Lawn size (sq ft)
        </div>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          step={50}
          placeholder="e.g. 5000"
          value={lawnSize}
          onChange={(e) => onLawnSizeChange(e.target.value)}
          aria-label="Lawn size in square feet"
          style={{
            width: "100%",
            padding: "10px 12px",
            border: `1px solid ${S_RULE}`,
            borderRadius: 8,
            background: "#fff",
            fontSize: 16,
            fontFamily: S_FONT,
            color: S_INK,
            minHeight: 44,
          }}
        />
        <div style={{ fontSize: 11.5, color: S_MUTED, marginTop: 4 }}>
          Optional — drives seed quantity
        </div>
      </div>
      </div>
    </div>
  )
}

// ── Form ───────────────────────────────────────────────────────────

export interface SeedingFormProps {
  initialZip?: string
  initialGrass?: string
  initialLawnSize?: number
  initialSunlight?: string
  /** Called whenever the live result changes, so the page can render downstream
   *  UI (analytics events, schema injection, etc.) without re-running the engine. */
  onResultChange?: (result: SeedingResult | null) => void
  /** Invoked when the verdict-driven CTA wants to open the email-reminder modal. */
  onRequestReminder?: (info: { reminderDate: Date; grassType: string; zip: string }) => void
}

export function SeedingForm({
  initialZip,
  initialGrass,
  initialLawnSize,
  initialSunlight,
  onResultChange,
  onRequestReminder,
}: SeedingFormProps) {
  const [zip, setZip] = useState<string>(initialZip ?? "")
  const [grass, setGrass] = useState<string>(() => {
    if (initialGrass && findSeedingCard(initialGrass)) return initialGrass
    return "Tall Fescue"
  })
  const [planningDate, setPlanningDate] = useState<string>("")
  const [lawnSizeInput, setLawnSizeInput] = useState<string>("")
  const lawnSize = (() => {
    const n = Number(lawnSizeInput)
    return Number.isFinite(n) && n > 0 ? n : undefined
  })()

  const region = useMemo(() => getRegionFromZipCode(zip), [zip])
  const zipKnown = zip.length === 5 && region !== "Unknown"

  const { savedInputs, saveInputs } = useSavedInputs()
  const seededFromUrl = useRef(false)
  const seededFromZip = useRef(false)
  const seededFromSaved = useRef(false)
  const editedLawnSize = useRef(false)

  // URL prefill — happens once.
  useEffect(() => {
    if (initialZip && !seededFromUrl.current) {
      setZip(initialZip)
      seededFromUrl.current = true
    }
    if (initialGrass && findSeedingCard(initialGrass)) {
      setGrass(initialGrass)
      seededFromZip.current = true
      seededFromSaved.current = true
    }
    if (initialLawnSize && !editedLawnSize.current && lawnSizeInput === "") {
      setLawnSizeInput(String(initialLawnSize))
    }
  }, [initialZip, initialGrass, initialLawnSize, lawnSizeInput])

  // Cross-site savedInputs (grass + size) — only if URL didn't seed them.
  useEffect(() => {
    if (seededFromSaved.current) return
    const savedGrass = savedInputs.grassType
    const savedSize = savedInputs.lawnSize
    if (savedGrass && findSeedingCard(savedGrass)) {
      setGrass(savedGrass)
      seededFromZip.current = true
    }
    if (typeof savedSize === "number" && savedSize > 0 && lawnSizeInput === "") {
      setLawnSizeInput(String(savedSize))
    }
    if (savedGrass || savedSize) seededFromSaved.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedInputs.grassType, savedInputs.lawnSize])

  // Region-based default — only if user hasn't picked yet.
  useEffect(() => {
    if (!zipKnown || seededFromZip.current) return
    // Region defaults are keyed on the legacy region set; degrade fine-grained
    // climate sub-regions (California, Florida, etc.) to their legacy parent.
    const def = SEEDING_REGION_DEFAULTS[toLegacyRegion(region)]
    if (def && findSeedingCard(def)) {
      setGrass(def)
      seededFromZip.current = true
    }
  }, [zipKnown, region])

  // Persist grass + size to savedInputs as the user picks.
  useEffect(() => {
    if (grass && findSeedingCard(grass)) saveInputs({ grassType: grass })
  }, [grass, saveInputs])
  useEffect(() => {
    if (lawnSize) saveInputs({ lawnSize })
  }, [lawnSize, saveInputs])

  // ── Live engine + verdict ──────────────────────────────────────
  const checkDate = useMemo(() => {
    if (!planningDate) return new Date()
    const parsed = new Date(planningDate)
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed
  }, [planningDate])

  const liveResult = useMemo<SeedingResult | null>(() => {
    if (!zipKnown) return null
    try {
      return calculateSeedingWindows(grass, zip, checkDate, {
        lawnSize,
        sunlight: initialSunlight ?? savedInputs.sunlight,
      })
    } catch {
      return null
    }
  }, [grass, zip, zipKnown, checkDate, lawnSize, initialSunlight, savedInputs.sunlight])

  const plan = useMemo(
    () => (liveResult ? buildSeedingViewPlan(liveResult, checkDate) : null),
    [liveResult, checkDate],
  )

  const publishedResult = useMemo<SeedingResult | null>(() => {
    if (!liveResult || !plan) return liveResult
    const canSeedNow = ["go", "go-secondary", "closing"].includes(plan.verdict)
    return {
      ...liveResult,
      canSeedNow,
      status: canSeedNow
        ? liveResult.status
        : plan.verdict === "too-late"
          ? "too_late"
          : "wrong_season",
      statusColor: canSeedNow
        ? liveResult.statusColor
        : plan.verdict === "too-late"
          ? "red"
          : "gray",
      statusMessage: `${plan.headline}. ${plan.detail}`,
      primaryWindow: plan.bestWindow,
    }
  }, [liveResult, plan])

  useEffect(() => {
    onResultChange?.(publishedResult)
  }, [publishedResult, onResultChange])

  // Analytics — fire once per settled input combination.
  useCalculatorAnalytics(
    "seeding",
    liveResult && plan
      ? {
          inputs: {
            zip_code: zip,
            grass_type: grass,
            planning_date: planningDate || undefined,
            lawn_size_sqft: lawnSize,
          },
          result: {
            region: liveResult.region,
            grass_season: liveResult.grassSeason,
            can_seed_now: liveResult.canSeedNow,
            status: liveResult.status,
            verdict: plan.verdict,
            climate_zone: liveResult.regionalData.climateZone,
            primary_window_start: liveResult.primaryWindow?.optimalStart
              ? new Date(liveResult.primaryWindow.optimalStart).toISOString().slice(0, 10)
              : undefined,
            primary_window_end: liveResult.primaryWindow?.optimalEnd
              ? new Date(liveResult.primaryWindow.optimalEnd).toISOString().slice(0, 10)
              : undefined,
            secondary_window_start: liveResult.secondaryWindow?.optimalStart
              ? new Date(liveResult.secondaryWindow.optimalStart).toISOString().slice(0, 10)
              : undefined,
            secondary_window_end: liveResult.secondaryWindow?.optimalEnd
              ? new Date(liveResult.secondaryWindow.optimalEnd).toISOString().slice(0, 10)
              : undefined,
          },
          location: zip,
          source: "live_update",
        }
      : null,
  )

  // ── Mobile detection + sticky bottom dock ────────────────────────
  const [narrow, setNarrow] = useState(false)
  const [formInView, setFormInView] = useState(true)
  const formRef = useRef<HTMLFormElement | null>(null)
  const dockRef = useRef<HTMLDivElement | null>(null)
  // Clears the Mediavine anchor/player beneath this bar. Must be the ad-only
  // inset, not the dock total, which includes this bar's own height.
  const adDockInset = useAdDockInset()
  useEffect(() => {
    if (typeof window === "undefined") return
    const mq = window.matchMedia("(max-width: 1023px)")
    const sync = () => setNarrow(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  useEffect(() => {
    if (!narrow) {
      setFormInView(true)
      return
    }
    const el = formRef.current
    if (!el || typeof IntersectionObserver === "undefined") return
    const observer = new IntersectionObserver(
      ([entry]) => setFormInView(entry.isIntersecting),
      { rootMargin: "0px 0px -25% 0px", threshold: 0 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [narrow])

  const showDock = narrow && formInView

  useEffect(() => {
    if (!showDock) {
      setBottomDockHeight(0)
      return
    }
    const el = dockRef.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      setBottomDockHeight(el.getBoundingClientRect().height)
    })
    ro.observe(el)
    setBottomDockHeight(el.getBoundingClientRect().height)
    return () => {
      ro.disconnect()
      setBottomDockHeight(0)
    }
  }, [showDock])

  // ── CTA handlers ──────────────────────────────────────────────────
  function buildCalendarEvent(window: SeedingWindow): CalendarEvent {
    return {
      id: `seeding-${window.season}-${grass.replace(/\s+/g, "-")}`,
      title: `${window.season === "fall" ? "Fall" : "Spring"} Lawn Seeding Window — ${grass}`,
      description: `Optimal seeding window for ${grass}${
        liveResult?.regionalData?.regionName
          ? ` in ${liveResult.regionalData.regionName}`
          : ""
      }. Soil temperature should be ${plan?.soilTemp ?? ""} for best germination.`,
      dueDate: new Date(window.optimalStart),
      reminderType: "seeding",
    }
  }

  function handleAddCalendar() {
    if (!plan?.bestWindow) return
    const event = buildCalendarEvent(plan.bestWindow)
    downloadICSFile([event], `seeding-${plan.bestWindow.season}-reminder.ics`)
  }

  function handleGoogleCalendar() {
    if (!plan?.bestWindow) return
    const event = buildCalendarEvent(plan.bestWindow)
    return generateGoogleCalendarUrl(event)
  }

  function handleRemindMe() {
    if (!plan?.reminderDate) return
    onRequestReminder?.({
      reminderDate: plan.reminderDate,
      grassType: grass,
      zip,
    })
  }

  // Render guards: until the engine produces a plan, show a placeholder.
  const ctaMode = plan?.ctaMode ?? "none"
  const tooLate = plan?.verdict === "too-late"

  const previewSlot = plan && liveResult ? (
    <SeedingPreview plan={plan} result={liveResult} lawnSize={lawnSize} />
  ) : (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        border: `1px solid ${S_RULE}`,
        boxShadow: "0 12px 40px rgba(0,0,0,0.04)",
        padding: 24,
        fontSize: 14,
        color: S_MUTED,
        lineHeight: 1.55,
      }}
    >
      Enter a ZIP code to see your seeding window.
    </div>
  )

  const previewSlotCompact = plan && liveResult ? (
    <SeedingPreview plan={plan} result={liveResult} compact />
  ) : (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        border: `1px solid ${S_RULE}`,
        boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
        padding: 14,
        fontSize: 13,
        color: S_MUTED,
        lineHeight: 1.55,
      }}
    >
      Enter a ZIP to see your seeding window.
    </div>
  )

  return (
    <form
      ref={formRef}
      onSubmit={(e) => e.preventDefault()}
      style={{
        background: S_BG,
        color: S_INK,
        fontFamily: S_FONT,
        borderRadius: 18,
        border: `1px solid ${S_RULE}`,
        boxShadow: "0 12px 40px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: narrow ? "minmax(0,1fr)" : "minmax(0,1fr) 460px",
          gap: narrow ? 24 : 32,
          padding: narrow ? "20px 18px 110px" : "32px 32px 36px",
        }}
      >
        {/* Mobile: live preview first */}
        {narrow && previewSlotCompact}

        {/* Form column */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            background: S_PAPER_DEEP,
            borderRadius: 14,
            padding: 12,
          }}
        >
          <SectionCard>
            <Label
              step={1}
              accent
              icon={<IcoPin size={13} />}
              hint={zipKnown ? `climate zone: ${region}` : "sets your climate zone"}
            >
              Where
            </Label>
            <ZipCodeInput
              value={zip}
              onChange={setZip}
              showLabel={false}
              enableGeolocation
              rememberLastZip
            />
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: S_MUTED,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <IcoLock size={11} /> Used once to set your climate. Never stored.
            </div>
          </SectionCard>

          <SectionCard>
            <GrassField
              value={grass}
              onChange={(g) => {
                seededFromZip.current = true
                setGrass(g)
              }}
            />
          </SectionCard>

          <SectionCard>
            <OptionalFields
              planningDate={planningDate}
              onPlanningDateChange={setPlanningDate}
              lawnSize={lawnSizeInput}
              onLawnSizeChange={(next) => {
                editedLawnSize.current = true
                setLawnSizeInput(next)
              }}
            />
          </SectionCard>
        </div>

        {/* Desktop right rail */}
        {!narrow && (
          <aside
            style={{
              alignSelf: "flex-start",
              position: "sticky",
              top: 96,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {previewSlot}
            {ctaMode === "calendar" && plan?.bestWindow && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button
                  type="button"
                  onClick={handleAddCalendar}
                  style={{
                    padding: "14px 18px",
                    background: S_BRAND,
                    color: "#fff",
                    border: "none",
                    borderRadius: 9999,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    boxShadow: "0 8px 20px -6px rgba(22,163,74,0.30)",
                    transition: "background 150ms",
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.background = S_BRAND_700)}
                  onMouseOut={(e) => (e.currentTarget.style.background = S_BRAND)}
                >
                  <IcoCal size={14} /> Add window to my calendar <IcoArrow size={14} />
                </button>
                <a
                  href={handleGoogleCalendar() ?? "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: 13,
                    color: S_BRAND_700,
                    textDecoration: "none",
                    fontWeight: 500,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "0 4px",
                  }}
                >
                  or add to Google Calendar →
                </a>
              </div>
            )}
            {ctaMode === "remind" && plan?.reminderDate && (
              <button
                type="button"
                onClick={handleRemindMe}
                style={{
                  padding: "14px 18px",
                  background: tooLate ? S_INK : S_BRAND,
                  color: "#fff",
                  border: "none",
                  borderRadius: 9999,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  boxShadow: tooLate ? "none" : "0 8px 20px -6px rgba(22,163,74,0.30)",
                  transition: "background 150ms",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = tooLate ? "#000" : S_BRAND_700)
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.background = tooLate ? S_INK : S_BRAND)
                }
              >
                Remind me {fmtMonthDay(plan.reminderDate)} <IcoArrow size={14} />
              </button>
            )}
            <div
              style={{
                fontSize: 12,
                color: S_MUTED,
                lineHeight: 1.55,
                display: "flex",
                gap: 8,
                padding: "0 4px",
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 2 }}>
                <IcoLock size={12} />
              </span>
              <span>
                Windows from regional frost-date data, last reviewed May 2026.
              </span>
            </div>
          </aside>
        )}
      </div>

      {/* Mobile sticky bottom dock */}
      {showDock && (
        <div
          ref={dockRef}
          style={{
            position: "fixed",
            bottom: adDockInset,
            left: 0,
            right: 0,
            zIndex: 30,
            boxShadow: "0 -8px 30px rgba(0,0,0,0.08)",
            background: "#fff",
            borderTop: `1px solid ${S_RULE}`,
            padding: "12px 16px",
            paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: tooLate ? "#dc2626" : S_BRAND_700,
                marginBottom: 2,
              }}
            >
              {plan?.pillLabel ?? "Best window"}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: S_INK,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {plan?.bestWindow
                ? `${fmtMonthDay(new Date(plan.bestWindow.optimalStart))} – ${fmtMonthDay(new Date(plan.bestWindow.optimalEnd))}`
                : "—"}
            </div>
          </div>
          {ctaMode === "calendar" && plan?.bestWindow && (
            <button
              type="button"
              onClick={handleAddCalendar}
              style={{
                padding: "12px 16px",
                background: S_BRAND,
                color: "#fff",
                border: "none",
                borderRadius: 9999,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                flexShrink: 0,
                boxShadow: "0 6px 14px -4px rgba(22,163,74,0.40)",
              }}
            >
              Add to calendar <IcoArrow size={13} />
            </button>
          )}
          {ctaMode === "remind" && plan?.reminderDate && (
            <button
              type="button"
              onClick={handleRemindMe}
              style={{
                padding: "12px 16px",
                background: tooLate ? S_INK : S_BRAND,
                color: "#fff",
                border: "none",
                borderRadius: 9999,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                flexShrink: 0,
                boxShadow: tooLate ? "none" : "0 6px 14px -4px rgba(22,163,74,0.40)",
              }}
            >
              Remind me <IcoArrow size={13} />
            </button>
          )}
        </div>
      )}
    </form>
  )
}
